/**
 * Calorie Deficit Calculator — Light DOM web component.
 *
 * Calculation mirrors the Webflow calorie-deficit calculator:
 * Mifflin-St Jeor (or Katch-McArdle when BF% is given),
 * goal-based weekly rate, staggered result reveal with count-up.
 */

const KG_PER_LB = 0.45359237
const LB_PER_KG = 2.2046226218
const CAL_PER_LB = 3500
const CM_PER_IN = 2.54
const DEBOUNCE_MS = 350
const COUNTUP_MS = 1800
const ROW_DELAY = 260
const INNER_DELAY = 280

const r1 = (n) => Math.round(n * 10) / 10
const plural = (v, u) => (Math.abs(v) === 1 ? u : u + 's')

class FelixCalorieDeficitCalculator extends HTMLElement {
	connectedCallback() {
		this._timer = null
		this._hasCalculated = false
		this._isMetric = false
		this._sex = 'female'
		this.cacheElements()
		this.bindEvents()
		this.maybeShowPresets()
	}

	disconnectedCallback() {
		this.unbindEvents()
	}

	cacheElements() {
		const $ = (s) => this.querySelector(s)
		const $$ = (s) => [...this.querySelectorAll(s)]

		this.sexRadios = $$('[name="cal-deficit-sex"]')
		this.age = $('[name="cal-deficit-age"]')
		this.ft = $('[name="cal-deficit-feet"]')
		this.inch = $('[name="cal-deficit-inches"]')
		this.cm = $('[name="cal-deficit-height-cm"]')
		this.lb = $('[name="cal-deficit-pounds"]')
		this.kg = $('[name="cal-deficit-weight-kg"]')
		this.goalLb = $('[name="cal-deficit-goal-pounds"]')
		this.goalKg = $('[name="cal-deficit-goal-kg"]')
		this.bf = $('[name="cal-deficit-body-fat"]')
		this.act = $('[name="cal-deficit-activity"]')

		this.imperialGroups = $$('[data-unit-group="imperial"]')
		this.metricGroups = $$('[data-unit-group="metric"]')
		this.switchLinks = $$('.form-input__switch-link')

		this.invalidEl = $('.cal-deficit-result__invalid')
		this.rows = $$('.cal-deficit-result__row')
		this.cta = $('.cal-deficit__cta')

		this.maintainCals = $('[data-result="maintain-cals"]')
		this.maintainDesc = $('[data-result="maintain-desc"]')
		this.cals30 = $('[data-result="30-cals"]')
		this.desc30 = $('[data-result="30-desc"]')
		this.cals15 = $('[data-result="15-cals"]')
		this.desc15 = $('[data-result="15-desc"]')

		// Prevent scroll from changing number inputs.
		$$('input[type="number"]').forEach((el) => {
			el.addEventListener('wheel', (e) => e.preventDefault(), { passive: false })
		})
	}

	bindEvents() {
		this._trigger = () => {
			clearTimeout(this._timer)
			this._timer = setTimeout(() => {
				if (this._hasCalculated || this.readyToCalc()) this.doCalc()
			}, DEBOUNCE_MS)
		}
		this._onSwitch = (e) => {
			e.preventDefault()
			this.setUnits(e.target.dataset.switchTo === 'metric')
			if (this._hasCalculated) this.doCalc()
		}
		this._onSexChange = (e) => {
			this._sex = e.target.value
			if (this._hasCalculated) this.doCalc()
		}

		const inputs = [this.age, this.ft, this.inch, this.cm, this.lb, this.kg, this.goalLb, this.goalKg, this.bf]
		inputs.forEach((el) => {
			if (!el) return
			el.addEventListener('input', this._trigger)
			el.addEventListener('change', this._trigger)
		})

		if (this.act) {
			this.act.addEventListener('change', this._trigger)
			this.act.addEventListener('input', this._trigger)
		}
		this.sexRadios.forEach((r) => r.addEventListener('change', this._onSexChange))
		this.switchLinks.forEach((link) => link.addEventListener('click', this._onSwitch))
	}

	unbindEvents() {
		const inputs = [this.age, this.ft, this.inch, this.cm, this.lb, this.kg, this.goalLb, this.goalKg, this.bf]
		inputs.forEach((el) => {
			if (!el) return
			el.removeEventListener('input', this._trigger)
			el.removeEventListener('change', this._trigger)
		})
		if (this.act) {
			this.act.removeEventListener('change', this._trigger)
			this.act.removeEventListener('input', this._trigger)
		}
		this.sexRadios.forEach((r) => r.removeEventListener('change', this._onSexChange))
		this.switchLinks.forEach((link) => link.removeEventListener('click', this._onSwitch))
	}

	setUnits(metric) {
		this._isMetric = !!metric
		this.imperialGroups.forEach((g) => g.classList.toggle('hidden', this._isMetric))
		this.metricGroups.forEach((g) => g.classList.toggle('hidden', !this._isMetric))
	}

	readyToCalc() {
		const a = +this.age?.value || 0
		const actVal = +this.act?.value || 0
		const stub = this.act?.value === 'stub-option'
		const haveH = this._isMetric ? +this.cm?.value > 0 : (+this.ft?.value > 0 || +this.inch?.value > 0)
		const haveW = this._isMetric ? +this.kg?.value > 0 : +this.lb?.value > 0
		const haveG = this._isMetric ? +this.goalKg?.value > 0 : +this.goalLb?.value > 0
		return a > 0 && haveH && haveW && haveG && actVal > 0 && !stub
	}

	doCalc() {
		if (!this.readyToCalc()) return

		const a = +this.age.value
		const actVal = +this.act.value
		const bfVal = +this.bf?.value || 0
		let cmVal, kgVal, goalKgVal, curr, goal

		if (!this._isMetric) {
			const ftv = +this.ft?.value || 0
			const inchv = +this.inch?.value || 0
			const lbv = +this.lb?.value || 0
			const glb = +this.goalLb?.value || 0
			cmVal = (ftv * 12 + inchv) * CM_PER_IN
			kgVal = lbv * KG_PER_LB
			goalKgVal = glb * KG_PER_LB
			curr = lbv
			goal = glb
		} else {
			cmVal = +this.cm?.value || 0
			kgVal = +this.kg?.value || 0
			goalKgVal = +this.goalKg?.value || 0
			curr = kgVal
			goal = goalKgVal
		}

		if (!kgVal || !cmVal) return

		// BMR: Katch-McArdle when BF% provided, Mifflin-St Jeor otherwise.
		const bmr = bfVal > 0
			? 370 + 21.6 * (kgVal * (1 - bfVal / 100))
			: 10 * kgVal + 6.25 * cmVal - 5 * a + (this._sex === 'male' ? 5 : -161)

		const tdee = bmr * actVal
		const unit = this._isMetric ? 'kg' : 'lb'
		const haveGoal = goal > 0
		const currKg = this._isMetric ? curr : curr * KG_PER_LB
		const goalKgReal = this._isMetric ? goal : goal * KG_PER_LB
		const deltaKg = haveGoal ? currKg - goalKgReal : 0
		const deltaUser = this._isMetric ? deltaKg : deltaKg * LB_PER_KG

		// Weekly rates for 30- and 15-week timelines.
		const rate30 = haveGoal ? deltaUser / 30 : 0
		const rate15 = haveGoal ? deltaUser / 15 : 0

		const dailyDelta = (r) => {
			const rLb = this._isMetric ? r * LB_PER_KG : r
			return (rLb * CAL_PER_LB) / 7
		}

		const kcalM = Math.round(tdee)
		const kcal30 = haveGoal ? Math.round(tdee - dailyDelta(rate30)) : kcalM
		const kcal15 = haveGoal ? Math.round(tdee - dailyDelta(rate15)) : kcalM

		// Always render the rows so negative numbers show behind the overlay (matches live).
		this.showResults(kcalM, kcal30, kcal15, curr, goal, rate30, rate15, unit, haveGoal)

		// A negative target is unsafe → overlay on + CTA hidden; otherwise overlay off + CTA shown.
		const hasNegative = kcalM < 0 || kcal30 < 0 || kcal15 < 0
		if (hasNegative) {
			this.showInvalid()
			this.hideCta()
		} else {
			this.hideInvalid()
			this.showCta()
		}

		// Once per page view, and deliberately without any entered value:
		// height, weight, age and sex are sensitive personal information.
		if (!this._hasCalculated) {
			window.felixTrack?.('calculator_complete', { calculatorName: 'calorie_deficit_calculator' })
		}

		this._hasCalculated = true
	}

	showInvalid() {
		this.invalidEl?.classList.add('is-visible')
	}

	hideInvalid() {
		this.invalidEl?.classList.remove('is-visible')
	}

	showResults(kcalM, kcal30, kcal15, curr, goal, rate30, rate15, unit, haveGoal) {
		// Copy text.
		this.maintainDesc.textContent = haveGoal ? `Stay at ${r1(curr)} ${unit}` : 'Stay at your current weight'

		const rateTxt = (r) => {
			if (!haveGoal || r === 0) return 'No weekly change'
			const abs = r1(Math.abs(r))
			return `${r > 0 ? 'Lose' : 'Gain'} ${abs} ${plural(abs, unit)} per week`
		}
		this.desc30.textContent = rateTxt(rate30)
		this.desc15.textContent = rateTxt(rate15)

		// Staggered row reveal + count-up.
		const cals = [kcalM, kcal30, kcal15]
		this.rows.forEach((row, i) => {
			setTimeout(() => {
				if (row.classList.contains('cal-deficit-result__row--30')) { row.classList.remove('bg-white'); row.classList.add('bg-rain-98') }
				if (row.classList.contains('cal-deficit-result__row--15')) { row.classList.remove('bg-white'); row.classList.add('bg-rain-95') }
				setTimeout(() => {
					const copy = row.querySelector('.cal-deficit-result__copy')
					if (copy) copy.style.opacity = '1'
					const calEl = row.querySelector('.cal-deficit-result__cals')
					if (calEl) {
						calEl.style.color = ''
						this.countUp(calEl, cals[i])
					}
				}, INNER_DELAY)
			}, i * ROW_DELAY)
		})
	}

	showCta() {
		const cta = this.cta
		if (!cta) return
		clearTimeout(this._ctaHideTimer)
		if (cta.dataset.shown === 'true') return
		cta.dataset.shown = 'true'
		cta.classList.remove('hidden')
		requestAnimationFrame(() => {
			const full = cta.scrollHeight || 200
			cta.style.height = '0'
			requestAnimationFrame(() => {
				cta.style.height = full + 'px'
				setTimeout(() => {
					cta.style.opacity = '1'
					cta.style.pointerEvents = 'auto'
					cta.style.height = 'auto'
				}, 350)
			})
		})
	}

	hideCta() {
		const cta = this.cta
		if (!cta || cta.dataset.shown !== 'true') return
		cta.dataset.shown = 'false'
		cta.style.height = cta.scrollHeight + 'px'
		cta.style.opacity = '0'
		cta.style.pointerEvents = 'none'
		requestAnimationFrame(() => {
			cta.style.height = '0'
		})
		this._ctaHideTimer = setTimeout(() => {
			cta.classList.add('hidden')
		}, 350)
	}

	maybeShowPresets() {
		const host = location.hostname || ''
		const isLocal = host === 'localhost' || host.endsWith('.local') || host === '127.0.0.1'
		const isStaging = host === 'felixforus.ca' || host.endsWith('.felixforus.ca')
		if (!isLocal && !isStaging) return

		const wrap = document.createElement('div')
		wrap.className = 'cal-deficit__presets mt-16'
		wrap.innerHTML =
			'<div class="form-input__label-row flex justify-between align-center mb-12">' +
				'<span class="form-input__label text-body-md text-slate-40">Debug presets (local &amp; staging only)</span>' +
			'</div>' +
			'<div class="cal-deficit__presets-buttons flex gap-8 flex-wrap">' +
			[
			['male_avg', 'Avg Male'],
			['female_avg', 'Avg Female'],
			['male_fit', 'Fit Male'],
			['female_active', 'Active Female'],
			['male_heavy', 'Heavy Male'],
			['negative', '\u26a0 Negative Cal'],
		]
			.map(
				([k, l]) =>
					`<button type="button" class="text-body-sm py-10 px-14 bg-slate-95 rounded-sm cursor-pointer flex-1" data-preset="${k}">${l}</button>`
			)
			.join('') +
			'</div>'

		const p = wrap.querySelector('.cal-deficit__presets-buttons')
		this.querySelector('.cal-deficit__form')?.appendChild(wrap)

		p.addEventListener('click', (e) => {
			const btn = e.target.closest('[data-preset]')
			if (!btn) return
			const presets = {
				male_avg: { sex: 'male', a: 32, ft: 5, inch: 11, lb: 200, goal: 180, act: '1.55' },
				female_avg: { sex: 'female', a: 28, ft: 5, inch: 5, lb: 165, goal: 145, act: '1.375' },
				male_fit: { sex: 'male', a: 30, ft: 5, inch: 10, lb: 175, goal: 165, act: '1.725' },
				female_active: { sex: 'female', a: 25, ft: 5, inch: 7, lb: 140, goal: 130, act: '1.725' },
				male_heavy: { sex: 'male', a: 40, ft: 6, inch: 1, lb: 280, goal: 220, act: '1.2' },
				negative: { sex: 'male', a: 45, ft: 5, inch: 6, lb: 300, goal: 100, act: '1.2' },
			}
			const d = presets[btn.dataset.preset]
			if (!d) return

			const sv = (el, v) => { if (el) el.value = v }
			sv(this.age, d.a)
			sv(this.ft, d.ft)
			sv(this.inch, d.inch)
			sv(this.lb, d.lb)
			sv(this.goalLb, d.goal)
			sv(this.act, d.act)

			// Sync metric fields.
			sv(this.cm, Math.round((d.ft * 12 + d.inch) * CM_PER_IN))
			sv(this.kg, Math.round(d.lb * KG_PER_LB))
			sv(this.goalKg, Math.round(d.goal * KG_PER_LB))

			// Pick sex via native radio.
			this._sex = d.sex
			this.sexRadios.forEach((r) => { r.checked = r.value === d.sex })

			this.setUnits(false)
			this._hasCalculated = true
			this.doCalc()
		})
	}

	countUp(el, end) {
		const start = Math.max(0, end - Math.max(300, end * 0.3))
		const t0 = performance.now()
		const ease = (t) => 1 - Math.pow(1 - t, 3)
		const run = (el._rid = (el._rid || 0) + 1)
		const step = (t) => {
			if (el._rid !== run) return
			const p = Math.min((t - t0) / COUNTUP_MS, 1)
			el.textContent = Math.floor(start + (end - start) * ease(p)).toLocaleString()
			if (p < 1) requestAnimationFrame(step)
		}
		requestAnimationFrame(step)
	}
}

customElements.define('felix-calorie-deficit-calculator', FelixCalorieDeficitCalculator)
